import type { FlaggedProductWithRestaurant, QtyFlagCustomerRequest, QtyFlagResolvedRestaurant, RestaurantForProductOverride } from '../../services/admin/adminService';
import type { AdminCustomerRequestDetailDto } from '../../services/customerRequests/types';

/** The legacy flag field can contain an audit trace id: never use it as a restaurant id without corroboration. */
export function matchingFlagRestaurant(rows: Pick<RestaurantForProductOverride, 'restaurantId'>[], legacyId: string): string | undefined {
  const restaurantIds = Array.from(new Set(rows.map(row => row.restaurantId)));
  return restaurantIds.includes(legacyId) ? legacyId : restaurantIds.length === 1 ? restaurantIds[0] : undefined;
}

/** An unanswered or unreviewed question blocks a second one; a completed, rejected or cancelled one doesn't. */
export function isActiveRequestStatus(status: string): boolean {
  return status === 'pending' || status === 'awaiting_review';
}

/** The real restaurants behind a flag. A setup flag's upload names its restaurant even before the server resolves it. */
export function qtyFlagRestaurants(flag: FlaggedProductWithRestaurant): QtyFlagResolvedRestaurant[] {
  const resolved = flag.resolvedRestaurants ?? [];
  if (resolved.length > 0 || !flag.setupSource) return resolved;
  return [{ restaurantId: flag.setupSource.restaurantId, restaurantName: flag.restaurantName }];
}

/** Every question behind a row: a de-duplicated row stands for several flags, possibly at several restaurants. */
export function qtyFlagRowRequests(flags: FlaggedProductWithRestaurant[]): QtyFlagCustomerRequest[] {
  return Array.from(new Map(flags.flatMap(flag => flag.customerRequests ?? []).map(request => [request.id, request])).values());
}

export type AskRestaurantOption = QtyFlagResolvedRestaurant & { activeRequestId?: string };

export function askRestaurantOptions(rows: QtyFlagResolvedRestaurant[], requests: Array<Pick<QtyFlagCustomerRequest, 'id' | 'restaurantId' | 'status'>>): AskRestaurantOption[] {
  const activeByRestaurant = new Map(requests.filter(request => isActiveRequestStatus(request.status)).map(request => [request.restaurantId, request.id]));
  return Array.from(new Map(rows.map(({ restaurantId, restaurantName }) => [restaurantId, { restaurantId, restaurantName, activeRequestId: activeByRestaurant.get(restaurantId) }])).values());
}

/** Questions from the live list that are about this flag's product, in the shape the flag itself carries. */
export function activeRequestsForFlag(items: AdminCustomerRequestDetailDto[], flag: Pick<FlaggedProductWithRestaurant, 'platform' | 'productName'>): QtyFlagCustomerRequest[] {
  const name = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
  return items.flatMap(item => {
    const question = item.question;
    if (question.type !== 'product_quantity' || name(question.product.name) !== name(flag.productName)) return [];
    if (question.product.platform && question.product.platform !== flag.platform) return [];
    return [{ id: item.id, restaurantId: item.restaurant.id, restaurantName: item.restaurant.name, status: item.status, createdAt: item.createdAt, dismissedByAll: item.recipients.length > 0 && item.recipients.every(person => person.dismissed) }];
  });
}

/** The question's source is the flag of that restaurant: a setup flag points at its upload, an email flag at its audit row. */
export function askSourceForRestaurant(flags: FlaggedProductWithRestaurant[], restaurantId: string): { kind: 'ai_checker' | 'setup_file'; id: string } {
  const owner = flags.find(flag => qtyFlagRestaurants(flag).some(row => row.restaurantId === restaurantId)) ?? flags[0];
  return { kind: owner.setupSource ? 'setup_file' : 'ai_checker', id: owner.restaurantId };
}

/** Opens the AI Checker on the upload behind a `setup-file:<restaurantId>:<fileId>` source: all-time, so an older upload's flag is visible. */
export function setupFileSourceLink(sourceId: string): string {
  const [prefix, restaurantId, fileId, ...rest] = sourceId.split(':');
  if (prefix !== 'setup-file' || !restaurantId || !fileId || rest.length > 0) return '/admin/testing/ai-checker';
  return `/admin/testing/ai-checker?${new URLSearchParams({ tab: 'issues', source: 'setup', fileId, restaurantId })}`;
}
